import { VisitorTypeHost } from "@gettactic/api";
import { useQuery } from "@tanstack/react-query";

export interface VisitorTypeHostsFilter {
  cursor?: string;
  search?: string;
  limit?: number;
}

interface VisitorTypeHostsClient {
  listHostsForType: (
    id: string,
    params?: VisitorTypeHostsFilter
  ) => Promise<{
    ok: boolean;
    result?: { elements: VisitorTypeHost[] } | null;
  }>;
}

export interface VisitorTypeHostsAPI {
  client: {
    stationSelf: VisitorTypeHostsClient;
    visitorInvitations: VisitorTypeHostsClient;
  };
}

export const VISITOR_TYPE_HOSTS_QUERY_KEY = "visitorTypeHosts";

/**
 * When anyone can be a host, hosts are only listed once this many characters
 * are typed so the host picker never exposes the whole directory (enforced
 * server-side too for stations and invitation links). A visitor type with
 * specific hosts lists them right away.
 */
export const MIN_HOST_SEARCH_LENGTH = 3;

export interface UseVisitorTypeHostsOptions {
  visitorTypeId: string;
  search?: string;
  limit?: number;
  enabled?: boolean;
  isInvitationMode?: boolean;
  /** Whether anyone can be a host, which requires a minimum search length */
  allUsersHosts?: boolean;
}

export function useVisitorTypeHosts(
  options: UseVisitorTypeHostsOptions,
  api: VisitorTypeHostsAPI
) {
  const {
    visitorTypeId,
    search,
    limit = 20,
    enabled = true,
    isInvitationMode = false,
    allUsersHosts = true
  } = options;

  return useQuery({
    queryKey: [
      VISITOR_TYPE_HOSTS_QUERY_KEY,
      visitorTypeId,
      search,
      limit,
      isInvitationMode
    ],
    queryFn: async () => {
      const client = isInvitationMode
        ? api.client.visitorInvitations
        : api.client.stationSelf;
      const response = await client.listHostsForType(visitorTypeId, {
        search,
        limit
      });
      if (response.ok && response.result?.elements) {
        return response.result.elements;
      }
      return [];
    },
    enabled:
      enabled &&
      !!visitorTypeId &&
      (!allUsersHosts || (search?.length ?? 0) >= MIN_HOST_SEARCH_LENGTH)
  });
}
